import { App, ConfigProvider } from "antd";
import { useTranslation } from "react-i18next";
import I18Provider from "../../i18n/I18Provider";
import { useSearchParams } from "react-router-dom";
import { getTaskDetails } from "../../actions/browserTest";
import { EMBEDD_VIEWER_STATES } from "../../actions/types";
import { sendWindowPostMessage } from "../../utills/common";
import { useMemo, useState, useEffect, useContext, createContext, useCallback } from "react";

const EmbedContext = createContext(null);
export const useEmbedContext = () => {
  const context = useContext(EmbedContext);

  if (!context) throw new Error("useEmbedContext context must be use inside EmbedProvider");

  return context;
};

export default function EmbedProvider({
  lid = "",
  tid = "",
  lang = "en",
  children = null,
  isPreview = false,
  taskDetails = null,
  color = "#01A3AE",
  onStart = () => {},
  onComplete = () => {},
  isMobileTest = false,
  isStandAlone = false,
  isBrowserTest = false,
}) {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [urlParams] = useSearchParams();
  const [task, setTask] = useState(null);

  // URL Params
  const logId = urlParams.get("lid") || lid || "";
  const taskId = urlParams.get("tid") || tid || "";
  const language = urlParams.get("lang") || lang || "en";
  const colorFromURLParam = urlParams.get("color") || "";

  const colorPrimary = useMemo(() => {
    if (colorFromURLParam && colorFromURLParam?.length >= 3) {
      return colorFromURLParam?.startsWith("#") ? colorFromURLParam : `#${colorFromURLParam}`;
    }

    return color || "#01A3AE";
  }, [color, colorFromURLParam]);

  useEffect(() => {
    if (taskDetails) {
      setTask(taskDetails);
      return;
    }

    // Get Task Details
    if (taskId) {
      getTaskDetails(logId, taskId)
        .then(({ task }) => setTask(task))
        .catch((err) => notification.error({ title: err?.message || "Something went wrong" }));
    }
  }, [logId, taskId, taskDetails, notification]);

  // On Task Start
  const onTaskStart = useCallback(
    (params = null) => {
      onStart(params);
      sendWindowPostMessage({ type: EMBEDD_VIEWER_STATES.STARTED });
    },
    [onStart]
  );

  // On Task End
  const onTaskComplete = useCallback(
    (payload = {}) => {
      onComplete(payload);
      sendWindowPostMessage({ type: EMBEDD_VIEWER_STATES.COMPLETED, payload });
    },
    [onComplete]
  );

  const memoizedValue = useMemo(
    () => ({
      t,
      task,
      logId,
      taskId,
      isPreview,
      colorPrimary,
      isMobileTest,
      isStandAlone,
      isBrowserTest,
      onTaskStart,
      onTaskComplete,
      lang: language,
    }),
    [t, logId, taskId, language, colorPrimary, isPreview, task, isMobileTest, isStandAlone, isBrowserTest, onTaskStart, onTaskComplete]
  );

  return (
    <EmbedContext.Provider value={memoizedValue}>
      <I18Provider lang={language}>
        <ConfigProvider
          theme={{
            hashed: true,
            token: { controlHeight: 36, colorPrimary },
            components: { Button: { colorPrimary } },
          }}
        >
          {children}
        </ConfigProvider>
      </I18Provider>
    </EmbedContext.Provider>
  );
}
